import { useEffect, useState } from "react";

/**
 * Builds a workspace file URL. `source` is a model-supplied, slash-separated relative path, so
 * each segment is encoded on its own: the separators have to survive, but a `#` or `?` inside a
 * filename would otherwise truncate the path to a fragment or open a query string — sending the
 * server a different file than the one the error message goes on to name.
 */
export function workspaceUrl(sessionId: string, source: string): string {
	return `/api/sessions/${encodeURIComponent(sessionId)}/workspace/${source.split("/").map(encodeURIComponent).join("/")}`;
}

/**
 * Component arguments are references, not data, so a renderer has to fetch whatever they point
 * at before it can show anything. A failed fetch has to say so and name the path it tried — an
 * empty table and a table that silently failed to load look identical otherwise, and that
 * ambiguity is exactly what these hooks exist to rule out.
 */
interface FetchState<T> {
	data: T | null;
	error: string | null;
	loading: boolean;
}

/**
 * Shared engine behind `useWorkspaceJson`/`useWorkspaceText`/`useWorkspaceExists`: fetch one
 * workspace file through the session's workspace route and hand the response to `parse`.
 * `source` is a path relative to the session workspace (`jobs/scored.json`, `documents/cv.pdf`,
 * ...); `undefined` means the caller has no reference to fetch yet, which is reported as an
 * error rather than silently staying in a loading state forever.
 */
function useWorkspaceFile<T>(
	sessionId: string,
	source: string | undefined,
	parse: (res: Response) => Promise<T>,
	init?: RequestInit,
	revision?: number,
): FetchState<T> {
	const [state, setState] = useState<FetchState<T>>({ data: null, error: null, loading: source !== undefined });

	useEffect(() => {
		if (!source) {
			setState({ data: null, error: "缺少 source 参数", loading: false });
			return;
		}
		let cancelled = false;
		setState({ data: null, error: null, loading: true });
		fetch(workspaceUrl(sessionId, source), { credentials: "include", ...init })
			.then(async (res) => {
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				return parse(res);
			})
			.then((data) => {
				if (!cancelled) setState({ data, error: null, loading: false });
			})
			.catch((err: unknown) => {
				if (!cancelled) setState({ data: null, error: `读取 ${source} 失败：${err instanceof Error ? err.message : String(err)}`, loading: false });
			});
		return () => {
			cancelled = true;
		};
	}, [sessionId, source, revision]);

	return state;
}

/** Fetches `source` as JSON — an object array, or `{items: [...]}` for Table; whatever shape for others. */
export function useWorkspaceJson<T>(sessionId: string, source: string | undefined): FetchState<T> {
	return useWorkspaceFile<T>(sessionId, source, (res) => res.json() as Promise<T>);
}

/**
 * Fetches `source` as raw text — Document uses this for `.md`, run through the Markdown renderer.
 * A changed `revision` fetches it again, for a file the agent keeps writing.
 */
export function useWorkspaceText(sessionId: string, source: string | undefined, revision?: number): FetchState<string> {
	return useWorkspaceFile<string>(sessionId, source, (res) => res.text(), undefined, revision);
}

/**
 * Confirms `source` exists without downloading it — a HEAD request, so Document can tell a
 * missing file apart from a real one before pointing an <iframe>/download link at it. The
 * workspace route's HEAD handling (Hono derives it from GET) returns the same status and headers
 * as GET would, body stripped, so this sees the same 404/403/409 a real fetch would.
 */
export function useWorkspaceExists(sessionId: string, source: string | undefined): FetchState<true> {
	return useWorkspaceFile<true>(sessionId, source, async () => true, { method: "HEAD" });
}
